import {
  getCleanSparkSpreadCal1,
  getCleanSparkSpreadSpot,
} from "$/api/market-monitoring/other-markets.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import ComposedLineGraph from "$/lib/components/graphs/ComposedLineGraph";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import { TextInput } from "$/lib/components/molecules";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import { displayMessage } from "$/lib/components/molecules/CustomToast/CustomToast.utils";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import { handleOnChangeDefaultGraphsTabs } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const KEYS_ORDER_FUTURE = [
  "Clean Spark Spread Cal+1",
  "CO2 (EUA) Y+1",
  "Elec Peak Fr Cal+1",
  "Gaz Base PEG Cal+1",
];

const KEYS_ORDER_SPOT = [
  "Clean Spark Spread Spot",
  "Spot CO2 (EUA)",
  "Spot PEG Day Ahead",
  "Spot Elec moyen journalier",
];

const COLORS: Record<string, string> = {
  "Clean Spark Spread Cal+1": "#767676",
  "CO2 (EUA) Y+1": "#a2a2a2",
  "Elec Peak Fr Cal+1": "#4f6496",
  "Gaz Base PEG Cal+1": "#6ba26f",
};

const SPOT_COLORS: Record<string, string> = {
  "Clean Spark Spread Spot": "#767676",
  "Spot CO2 (EUA)": "#c7c9c9",
  "Spot PEG Day Ahead": "#b0cc7f",
  "Spot Elec moyen journalier": "#b7dadb",
};

const SparkSpreadCharts = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [filters, setFilters] = useState({
    gasYield: 0.5,
    gasEmission: 0.455,
  });

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const { data, isLoading } = useQuery({
    queryKey: ["get-other-markets-spark-spread-future", dateRange, filters],
    queryFn: () =>
      getCleanSparkSpreadCal1({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        ...filters,
      }),
  });

  // const prevDataRef = useRef<any>(undefined);

  // const toDisplay = useMemo(() => {
  //   if (data) prevDataRef.current = data;

  //   return prevDataRef.current;
  // }, [data]);

  const { data: spotData } = useQuery({
    queryKey: ["get-other-markets-spark-spread-spot", dateRange, filters],
    queryFn: () =>
      getCleanSparkSpreadSpot({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        ...filters,
      }),
  });

  const keys = useMemo(
    () => getArrayKeys(data ?? [], "date", KEYS_ORDER_FUTURE),
    [data],
  );

  const spotKeys = useMemo(
    () => getArrayKeys(spotData ?? [], "date", KEYS_ORDER_SPOT),
    [spotData],
  );

  const colors: Record<string, string> = keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: COLORS[key],
    }),
    {},
  );

  const spotColors = spotKeys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: SPOT_COLORS[key],
    }),
    {},
  );

  const [graphVisibility, setGraphVisibility] = useState<{
    data: GraphFilterItemsProps[];
    spotData: GraphFilterItemsProps[];
  }>({
    data: [],
    spotData: [],
  });

  const handleOnChangeGraphVisiblity = (
    dataKey: "data" | "spotData",
    key: string,
    value: boolean,
  ) => {
    setGraphVisibility((prev) => ({
      ...prev,
      [dataKey]: prev[dataKey].map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    }));
  };

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility({
      data: keys.map((key) => ({
        name: key,
        color: COLORS[key],
        isVisible: true,
      })),
      spotData: spotKeys.map((key) => ({
        name: key,
        color: SPOT_COLORS[key],
        isVisible: true,
      })),
    });
  }, [keys, spotKeys]);

  return (
    <DisplayBox
      title="Clean Spark Spreads"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      Icon={LineChartIcon}
      theme="orange"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight justify="between" className="gap-2">
            <Flexbox fullWidth className="h-1/2">
              <ComposedLineGraph
                data={data ?? []}
                title="Clean Spark Spread Future (Cal+1) France"
                xAxisDataKey="date"
                areaChartDataKey="Clean Spark Spread Cal+1"
                keys={keys}
                colors={colors}
                graphVisibility={graphVisibility.data}
                theme="orange"
                areaChartColor="#767676"
                withGradientEffect={false}
                unit=" €/MWh"
                heightPercent={43}
                connectNulls
                leftMargin={-10}
                rightMargin={-10}
                displayUnits={{
                  "CO2 (EUA) Y+1": " €/t",
                }}
                secondYAxis={{
                  type: "LINE",
                  dataKey: "CO2 (EUA) Y+1",
                  color: COLORS["CO2 (EUA) Y+1"] ?? "#a2a2a2",
                  unit: " €/t",
                }}
              />
            </Flexbox>

            <Flexbox fullWidth className="h-1/2">
              <ComposedLineGraph
                data={spotData ?? []}
                title="Clean Spark Spread Spot France"
                xAxisDataKey="date"
                areaChartDataKey="Clean Spark Spread Spot"
                keys={spotKeys}
                colors={spotColors}
                graphVisibility={graphVisibility.spotData}
                theme="orange"
                areaChartColor="#767676"
                withGradientEffect={false}
                unit=" €/MWh"
                heightPercent={43}
                connectNulls
                leftMargin={-10}
                rightMargin={-10}
                displayUnits={{
                  "Spot CO2 (EUA)": " €/t",
                }}
                secondYAxis={{
                  type: "LINE",
                  dataKey: "Spot CO2 (EUA)",
                  color: SPOT_COLORS["Spot CO2 (EUA)"] ?? "#c7c9c9",
                  unit: " €/t",
                }}
              />
            </Flexbox>
          </Flexbox>
        )}

        <FiltersDisplayBox className="min-w-[20%]">
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="orange"
          />

          <Flexbox className="gap-4">
            <TextInput
              label="Rendement Gaz (%)"
              type="number"
              className="w-[100px]"
              labelClassName="whitespace-nowrap text-base font-semibold text-secondary"
              value={filters.gasYield * 100}
              onChange={(e) => {
                const value = parseFloat(e.target.value);

                if (value >= 0 && value <= 100) {
                  setFilters((prev) => ({
                    ...prev,
                    gasYield: value / 100,
                  }));
                  return;
                }

                displayMessage(
                  "Rendement Gaz doit être entre 0 et 100",
                  "warning",
                );
              }}
            />
            <TextInput
              label="Facteur d'Emission (gCO2/MWh)"
              type="number"
              className="w-[100px]"
              labelClassName="text-base font-semibold text-secondary"
              value={filters.gasEmission}
              onChange={(e) => {
                const value = parseFloat(e.target.value);

                if (value >= 0) {
                  setFilters((prev) => ({ ...prev, gasEmission: value }));
                  return;
                }

                displayMessage(
                  "Facteur d'Emission doit être supérieur à 0",
                  "warning",
                );
              }}
            />
          </Flexbox>

          <Flexbox className="gap-4 overflow-y-auto">
            <GraphFilter
              title="Légende CSS Future (Cal+1)"
              data={graphVisibility.data}
              handleOnChange={(...values) =>
                handleOnChangeGraphVisiblity("data", ...values)
              }
            />

            <GraphFilter
              title="Légende CSS Spot"
              data={graphVisibility.spotData}
              handleOnChange={(...values) =>
                handleOnChangeGraphVisiblity("spotData", ...values)
              }
            />
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default SparkSpreadCharts;
